/**
 * src/utils/errorReporter.js
 *
 * Silent background error reporter.
 *
 * Captures a breadcrumb trail of user actions (clicks, navigation) and
 * sends error reports to the error-log-ingest Azure Function as fire-and-
 * forget requests. The user is never shown any indication that reporting
 * happened.
 *
 * Dedup, in two stages:
 *
 *   1. Here — errors with the same fingerprint are SENT at most once per page
 *      session, so a rendering loop cannot fire hundreds of requests. But a
 *      repeat is no longer silently discarded: occurrences are COUNTED during
 *      a short batching window and the count travels with the report. A burst
 *      of ten refused writes from one bulk action is the most diagnostic fact
 *      about it, and it used to be destroyed here — the first was reported,
 *      the other nine dropped, and nothing recorded that there had been nine.
 *
 *   2. Server-side, in azure-functions/shared/bugReport.js — the same defect
 *      reported from a LATER page session (a reload, another user, another
 *      day) is folded onto the GitHub issue that already tracks it instead of
 *      filing another one.
 *
 * Stage 1 alone was all that ever existed, which is why one bug could occupy
 * a week of identical issues in the backlog.
 */

import { getFunctionUrl } from '../api/functionUrl.js';
import { getReportingToken } from '../api/sessionToken.js';

// ── Breadcrumb collector ───────────────────────────────────────────────────

const MAX_BREADCRUMBS = 30;
const breadcrumbs = [];

function pushCrumb(type, data) {
  breadcrumbs.push({ type, at: new Date().toISOString(), ...data });
  if (breadcrumbs.length > MAX_BREADCRUMBS) breadcrumbs.shift();
}

// Capture every click — log tag, trimmed visible text and current path.
document.addEventListener(
  'click',
  (e) => {
    const el = e.target;
    if (!el) return;
    const tag = el.tagName?.toLowerCase() ?? '?';
    const text = (el.textContent ?? el.getAttribute?.('aria-label') ?? '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 60);
    const role = el.getAttribute?.('role') ?? '';
    pushCrumb('click', { tag, text, role, path: location.pathname });
  },
  true // capture phase — fires before React handlers, never suppressed
);

// Capture navigation via the History API (React Router uses pushState).
const _origPushState = history.pushState.bind(history);
history.pushState = function (...args) {
  _origPushState(...args);
  pushCrumb('navigate', { to: String(args[2] ?? location.href).slice(0, 200) });
};
window.addEventListener('popstate', () => {
  pushCrumb('navigate', { to: location.href.slice(0, 200) });
});

// ── Fingerprinting & dedup ─────────────────────────────────────────────────

/** Already-SENT fingerprints for this page session. */
const reported = new Set();

/**
 * Reports waiting out their batching window, keyed by fingerprint.
 * @type {Map<string, { payload: object, count: number, firstSeen: string, timer: number }>}
 */
const pending = new Map();

// How long to hold a first occurrence while counting repeats of it. Long
// enough to absorb a burst that a single user action fires in parallel (a
// bulk apply across N records issues N requests at once); short enough that a
// report still lands promptly, and that a user who navigates away immediately
// is covered by the unload flush below.
const BATCH_WINDOW_MS = 1500;

/**
 * Produce a stable short hash from the error message + first stack frame.
 * Identical errors in the same session are only sent once.
 */
function fingerprint(message, stack) {
  const key = `${message}::${(stack ?? '').split('\n').slice(0, 3).join('|')}`;
  let h = 0x811c9dc5; // FNV-1a 32-bit offset basis
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 0x01000193) | 0;
  }
  return (h >>> 0).toString(36);
}

// ── Runscript tenant exclusion ──────────────────────────────────────────────

// beta-cmcpbl is the shared tenant playscripts run against (see
// playwright-guides/qa/lib/qa-base.js SAFE_HOST_PATTERNS). Playscript runs —
// especially the QA*E error-provocation scripts — deliberately trigger errors
// that get captured and triaged through the playscript pipeline's own GitHub
// issue flow (npm run playscript:issue). Auto-reporting the same errors here
// would double-file them, so reports from that tenant are suppressed.
//
// But the founder ALSO tests by hand on that same tenant, and the exclusion
// used to be by HOSTNAME ALONE — so every genuine error a human hit there was
// dropped on the floor as well. The host is not the discriminator; who is
// driving the browser is. What is suppressed now is an AUTOMATED session on
// that host, and nothing else.
const RUNSCRIPT_TENANT_HOST = 'beta-cmcpbl.tryggr.dk';

// The token playwright-guides appends to the browser's real user-agent string
// (playwright-guides/config/base.js — PLAYSCRIPT_UA_MARKER). It travels in the
// User-Agent HEADER, which is what lets the server mirror this decision from
// the request itself rather than from anything the page claims about itself.
// Keep the literal identical on both sides.
const PLAYSCRIPT_UA_MARKER = 'TryggrPlayscript';

/**
 * Is this page being driven by automation rather than by a person?
 *
 * Two independent signals, because either one alone has a failure mode that
 * lands on the wrong side:
 *
 *   1. `navigator.webdriver` — set by every WebDriver/CDP-driven browser
 *      (Playwright chromium, firefox and webkit all report true), so it holds
 *      even for a playscript checkout too old to know about the UA marker.
 *   2. The UA marker — explicit, set by our own harness, and the only one of
 *      the two the SERVER can also see. It covers a browser that masks
 *      `navigator.webdriver`.
 *
 * Note this deliberately does NOT use an init script to read a flag the
 * harness injected: this app's `script-src` carries no 'unsafe-inline' and
 * Chromium enforces it against CDP-injected init scripts, so a marker set that
 * way never arrives (PL-097). The UA is set at the context level and is not
 * subject to CSP.
 *
 * Fails OPEN — an unreadable `navigator` reports "not automated", so a report
 * is sent rather than silently discarded. Sending one is recoverable (the
 * server-side guard and bugReport's dedup both still apply); dropping a real
 * human's crash is the bug this whole change exists to fix.
 */
function isAutomatedSession() {
  try {
    if (navigator.webdriver === true) return true;
    return String(navigator.userAgent || '').includes(PLAYSCRIPT_UA_MARKER);
  } catch {
    return false;
  }
}

/** Suppressed only where a playscript is the one provoking the error. */
function isPlayscriptDrivenReport() {
  try {
    return location.hostname === RUNSCRIPT_TENANT_HOST && isAutomatedSession();
  } catch {
    return false;
  }
}

// ── Public API ─────────────────────────────────────────────────────────────

/**
 * Report an error silently in the background.
 *
 * @param {object} opts
 * @param {string}  opts.message        — Error message
 * @param {string}  [opts.stack]        — Stack trace string
 * @param {string}  [opts.componentStack] — React component stack (ErrorBoundary only)
 * @param {string}  [opts.context]      — Extra label, e.g. 'ErrorBoundary' | 'unhandledrejection'
 */
/**
 * Send one batched report. Never throws.
 */
function send(entry) {
  // A session JWT, or a portal JWT when the surface is one of the magic-link
  // portals. Either way the tenant comes from the SERVER's verification of the
  // token, never from this payload — reporting introduces no new way to name a
  // tenant. Without any credential the report cannot be attributed at all, and
  // an unattributable report is not filed.
  const token = getReportingToken();
  if (!token) return;

  const payload = {
    ...entry.payload,
    occurrences: entry.count,
    firstSeen: entry.firstSeen,
  };

  // keepalive ensures delivery even if the error caused a page navigation/unload.
  // .catch(() => {}) swallows network failures silently.
  fetch(getFunctionUrl('error-log-ingest'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {});
}

/**
 * Send everything still inside its batching window.
 *
 * Called on page hide/unload so a user who clicks away during the window does
 * not take the report with them — the whole point of reporting in the
 * background is that it does not depend on the user staying put.
 */
function flushPending() {
  for (const [fp, entry] of pending) {
    clearTimeout(entry.timer);
    pending.delete(fp);
    reported.add(fp);
    send(entry);
  }
}

window.addEventListener('pagehide', flushPending);
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') flushPending();
});

/**
 * Report an error silently in the background.
 *
 * @param {object}  opts
 * @param {string}  opts.message        — Error message
 * @param {string}  [opts.stack]        — Stack trace string
 * @param {string}  [opts.componentStack] — React component stack (ErrorBoundary only)
 * @param {string}  [opts.context]      — Extra label, e.g. 'ErrorBoundary' | 'unhandledrejection'
 * @param {string}  [opts.dedupKey]     — Overrides the message+stack fingerprint.
 *   Pass this when the message alone does not identify the defect, or when the
 *   stack would wrongly split one defect into several. A 409 conflict is the
 *   motivating case: every refusal carries the same generic message, so the
 *   endpoint and the server's conflict reason are what actually distinguish
 *   "two users edited one record" from "ten writes raced to create a row".
 */
export function reportError({ message, stack, componentStack, context, dedupKey }) {
  if (!message) return;
  if (isPlayscriptDrivenReport()) return;

  const fp = fingerprint(dedupKey || message, dedupKey ? '' : stack);

  // Already sent this page session. The server folds a later occurrence onto
  // the open issue, so re-sending would buy nothing but traffic.
  if (reported.has(fp)) return;

  const existing = pending.get(fp);
  if (existing) {
    // Inside the window — count it. Deliberately does NOT extend the timer: a
    // continuous stream of errors must still produce a report promptly rather
    // than being deferred for as long as it keeps happening.
    existing.count += 1;
    return;
  }

  pending.set(fp, {
    count: 1,
    firstSeen: new Date().toISOString(),
    payload: {
      message,
      stack: stack ?? null,
      componentStack: componentStack ?? null,
      context: context ?? null,
      url: location.href,
      path: location.pathname,
      browser: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      breadcrumbs: breadcrumbs.slice(), // snapshot at time of FIRST occurrence
      fingerprint: fp,
    },
    timer: setTimeout(() => {
      const entry = pending.get(fp);
      if (!entry) return;
      pending.delete(fp);
      reported.add(fp);
      send(entry);
    }, BATCH_WINDOW_MS),
  });
}

/** Test seam — drop all batching/dedup state. */
export function __resetErrorReporterForTests() {
  for (const entry of pending.values()) clearTimeout(entry.timer);
  pending.clear();
  reported.clear();
}
